import config from '../config/config';

const JSONP_RANDOM_MAX = config.jsonp.randomMax;
const JSONP_CALLBACK_PREFIX = config.jsonp.callbackPrefix;
const JSONP_TIMEOUT_MS = config.jsonp.timeoutMs;

const DEFAULT_COORD_PRECISION = config.map.defaultCoordPrecision;

const SCRIPT_TAG = config.common.domTags.script;

const UNKNOWN_STATION_NAME = config.search.station.unknownStationName;
const CATEGORY1_NAME_STATION = config.search.station.categoryTypeStationJp;
const TAXONOMY_STATION = config.search.station.categoryTypeStation;
const DEFAULT_RADIUS_METERS = config.search.station.defaultRadiusMeters;
const MAX_RADIUS_METERS = config.search.station.maxRadiusMeters;

/**
 * docsをアプリ内部で扱いやすい形に変換
 */
function convertStationDocs(docs) {
  return (docs || []).map(item => ({
    name: item.name || item.station_name || item.poi_name || UNKNOWN_STATION_NAME,
    poi_name: item.name || item.station_name || item.poi_name || UNKNOWN_STATION_NAME,
    address: item.address || item.addr || '',
    phone_number: item.phone_number || item.phone || '',
    lat: item.lat,
    lon: item.lon,
    distance: item.distance,
    taxonomy: TAXONOMY_STATION,
    yomi: item.yomi || item.reading || '',
    category1_name: [CATEGORY1_NAME_STATION],
    station_code: item.poi_code,
    line_name: item.line_name
  }));
}

/**
 * JSONPでURLを叩いてdocsを取得する（失敗時は空配列）
 */
function fetchDocsByJsonp(urlBase) {
  return new Promise((resolve) => {
    try {
      // JSONP用のコールバック名をランダム生成（複数同時呼び出しへの衝突対策）
      const callbackName = JSONP_CALLBACK_PREFIX + Math.floor(Math.random() * JSONP_RANDOM_MAX);
      // API URL組立（json.wrf=callbackNameを付与してJSONP形式）
      const url = `${urlBase}&json.wrf=${callbackName}`;

      // JSONP実行用のscript要素を作成
      const script = document.createElement(SCRIPT_TAG);
      script.src = url;
      script.async = true;

      let timeoutId;

      const cleanup = () => {
        clearTimeout(timeoutId);
        delete window[callbackName];
        if (script.parentNode) {
          document.body.removeChild(script);
        }
      };

      // JSONPのコールバック（サーバがこの関数を呼ぶ）
      window[callbackName] = function (data) {
        cleanup();
        try {
          // レスポンス形式の揺れ（data.docs / data.response.docs）に対応
          const docs = data?.docs || data?.response?.docs || [];
          resolve(docs);
        } catch (error) {
          resolve([]);
        }
      };

      // タイムアウト（callbackが呼ばれない場合に備える）
      timeoutId = setTimeout(() => {
        cleanup();
        resolve([]);
      }, JSONP_TIMEOUT_MS);

      // scriptロードエラー
      script.onerror = () => {
        cleanup();
        resolve([]);
      };

      document.body.appendChild(script);
    } catch (error) {
      resolve([]);
    }
  });
}

/**
 * 駅検索関数（JSONP方式）
 * @returns {Promise<Array>} 駅検索結果
 */
export async function searchStationAPI(center, radius = DEFAULT_RADIUS_METERS) {
  return new Promise((resolve) => {
    try {
      const lat = center.lat.toFixed(DEFAULT_COORD_PRECISION);
      const lng = center.lng.toFixed(DEFAULT_COORD_PRECISION);

      const urlBase = config.api.stationLocal(lng, lat, radius);
      fetchDocsByJsonp(urlBase).then((docs) => {
        try {
          // docsが空の場合、最大半径にして再度実行（既存挙動維持）
          if (!docs.length) {
            if (radius < MAX_RADIUS_METERS) {
              searchStationAPI(center, MAX_RADIUS_METERS).then(resolve);
              return;
            }
            resolve([]);
            return;
          }

          resolve(convertStationDocs(docs));
        } catch (error) {
          resolve([]);
        }
      });
    } catch (error) {
      // 初期化エラーでも空配列
      resolve([]);
    }
  });
}

/**
 * 駅のPOIコード(ST...) で駅検索を行う関数（JSONP方式）
 * @returns {Promise<Array>} 駅検索結果（駅コードなので結果はあれば1件）
 */
export async function searchStationPoiAPI(query) {
  return new Promise((resolve) => {
    try {
      const urlBase = config.api.stationPoiCode(query);

      fetchDocsByJsonp(urlBase).then((docs) => {
        try {
          resolve(convertStationDocs(docs));
        } catch (error) {
          resolve([]);
        }
      });
    } catch (error) {
      // 初期化エラーでも空配列
      resolve([]);
    }
  });
}